import { Injectable } from '@angular/core';
import { Action, createFeatureSelector, createSelector, MemoizedSelector, select, Store } from '@ngrx/store';
import * as get from 'lodash/get';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { ActionConstants } from './action.constants';
import { Config } from './config.interface';
import { EmptyStoreDefinition } from './models/empty-store-definition.model';
import { StoreEntity } from './models/store-entity.model';
import { State } from './state.interface';

@Injectable()
export class StoreService {
	private selectors: any = {};

	constructor(private store: Store<any>) { }

	/**
	 * Return all entities of the stated config slice
	 */
	public selectAll<T>(config: Config<T>): Observable<State<T>> {
		if (get(config, 'name')) {
			return this.store.pipe(select(this.getSelector<T>(config)));
		} else {
			return of(new EmptyStoreDefinition() as any);
		}
	}

	/**
	 * Return entities of the stated config slice filtered by specific id
	 */
	public selectById<T>(config: Config<T>, id: string): Observable<StoreEntity<T>> {
		if (get(config, 'name') && id) {
			return this.store.pipe(
				select<any, any, StoreEntity<T>>(
					createSelector(
						this.getSelector<T>(config),
						state => {
							return state.entities[id] ? { [id]: state.entities[id] } as StoreEntity<T> : undefined;
						}
					), {}
				),
				distinctUntilChanged()
			);
		}
	}

	/**
	 * Return entities of the stated config slice filtered by specific ids
	 */
	public selectByIds<T>(config: any, ids: Array<string>): Observable<State<T>> {
		if (get(config, 'name') && ids && ids.length) {
			return this.store.pipe(
				select<any, any, T>(
					createSelector(
						this.getSelector<T>(config),
						state => {
							// TODO: This probably isn't the correct solution - needs more research!
							const selection: any = {
								isLoading: (state as any).isLoading,
								ids: [],
								entities: {}
							};
							ids.forEach(id => {
								if (state['entities']?.[id]) {
									selection.ids.push(id);
									selection.entities[id] = state['entities'][id];
								}
							});
							return selection;
						}
					), {}
				),
				distinctUntilChanged((x: any, y: any) => {
					if (Object.keys(x.entities).length !== Object.keys(y.entities).length) {
						return false;
					}
					if (x.isLoading !== y.isLoading) {
						return false;
					}
					return Object.keys(x.entities).every(xKey => x.entities[xKey] === y.entities[xKey]);
				})
			);
		}
	}

	/**
	 * Private function to return selector for a config
	 */
	private getSelector<T>(config: Config<T>): MemoizedSelector<any, State<T>> {
		if (get(config, 'name')) {
			const name = config.name;
			const currentSelector = this.selectors && this.selectors[name];
			if (!currentSelector) {
				const thisState = createFeatureSelector<State<T>>(name);
				if (config.storeAsList) {
					this.selectors[name] = createSelector(
						thisState,
						state => state
					);
				} else {
					this.selectors[name] = createSelector(
						thisState,
						state => state?.['object']
					);
				}
			}
			return this.selectors[config.name];
		} else {
			throw new Error('Error in getSelector: name cannot be empty!');
		}
	}

	/**
	 * Not handled in reducer, for use in effects only
	 */
	public fetch<T>(config: Config<T>, payload?: T): void {
		this.store.dispatch(this.generateAction(ActionConstants.FETCH, config, payload));
	}

	/**
	 * Add one entity to the collection
	 */
	public addOne<T>(config: Config<T>, payload: T): void {
		this.store.dispatch(this.generateAction(ActionConstants.ADD_ONE, config, payload));
	}

	/**
	 * Add multiple entities to the collection
	 */
	public addMany<T>(config: Config<T>, payload: T): void {
		this.store.dispatch(this.generateAction(ActionConstants.ADD_MANY, config, payload));
	}

	/**
	 * Replace current collection with provided collection
	 */
	public addAll<T>(config: Config<T>, payload: T[]): void {
		this.store.dispatch(this.generateAction(ActionConstants.ADD_ALL, config, payload));
	}

	/**
	 * Remove one entity from the collection
	 */
	public removeOne<T>(config: Config<T>, payload: string | T): void {
		this.store.dispatch(this.generateAction(ActionConstants.REMOVE_ONE, config, payload));
	}

	/**
	 * Remove multiple entities from the collection
	 */
	public removeMany<T>(config: Config<T>, payload: string[]): void {
		this.store.dispatch(this.generateAction(ActionConstants.REMOVE_MANY, config, payload));
	}

	/**
	 * Clear entity collection
	 */
	public removeAll<T>(config: Config<T>, payload: string): void {
		this.store.dispatch(this.generateAction(ActionConstants.REMOVE_ALL, config, payload));
	}

	/**
	 * Update one entity in the collection
	 */
	public updateOne<T>(config: Config<T>, payload: T): void {
		this.store.dispatch(this.generateAction(ActionConstants.UPDATE_ONE, config, payload));
	}

	/**
	 * Update multiple entities in the collection
	 */
	public updateMany<T>(config: Config<T>, payload: T): void {
		this.store.dispatch(this.generateAction(ActionConstants.UPDATE_MANY, config, payload));
	}

	/**
	 * Add or Update one entity in the collection
	 * Recommend to use
	 */
	public upsertOne<T>(config: Config<T>, payload: T): void {
		this.store.dispatch(this.generateAction(ActionConstants.UPSERT_ONE, config, payload));
	}

	/**
	 * Add or Update multiple entities in the collection
	 * Recommend to use
	 */
	public upsertMany<T>(config: Config<T>, payload: T[]): void {
		this.store.dispatch(this.generateAction(ActionConstants.UPSERT_MANY, config, payload));
	}

	/**
	 * Used for simple object to store
	 */
	public loadOne<T>(config: Config<T>, payload: T): void {
		this.store.dispatch(this.generateAction(ActionConstants.LOAD_ONE, config, payload));
	}

	/**
	 * No Action
	 */
	public noAction<T>(config: Config<T>): void {
		this.store.dispatch(this.generateAction(ActionConstants.NO_ACTION, config, null));
	}

	private generateAction<K, T>(type: string, config: Config<K> | string, payloadInput: T): Action {
		if (!type || !config) {
			throw new Error('Error in generateAction: type and name cannot be empty!');
		}

		const name: string = typeof config !== 'string' && config.hasOwnProperty('name') ? config['name'] : config as string;

		class NewAction implements Action {
			public readonly type = type.toUpperCase() + name.toUpperCase();
			constructor(public payload: T) { }
		}
		return new NewAction(payloadInput);
	}
}
